import { useCallback, useEffect, useMemo, useState } from "react";
import { api, STATUS_LABEL, hr, hrDateTime, statusBadgeVariant } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Check, X, Loader2, HomeIcon, Search, ArrowDownWideNarrow, ArrowUpWideNarrow, CheckCheck } from "lucide-react";
import EmployeeHistorySheet from "../components/EmployeeHistorySheet";
import { ApprovalDecisionDialog, BulkRejectDialog } from "../components/ApprovalDecisionDialog";
import BehalfVacationDialog from "../components/BehalfVacationDialog";
import { toast } from "sonner";

export default function Approvals() {
  const { user } = useAuth();
  const [vacation, setVacation] = useState([]);
  const [wfh, setWfh] = useState([]);
  const [busy, setBusy] = useState(null);
  const [comment, setComment] = useState("");
  const [open, setOpen] = useState(null); // {id, action, type}
  const [historyFor, setHistoryFor] = useState(null);
  const [search, setSearch] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [selectedVac, setSelectedVac] = useState(new Set());
  const [selectedWfh, setSelectedWfh] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkRejectOpen, setBulkRejectOpen] = useState(null); // 'vacation' | 'wfh' | null
  const [bulkRejectReason, setBulkRejectReason] = useState("");
  // Iter91: team-lead behalf submission
  const [behalfOpen, setBehalfOpen] = useState(false);

  const load = useCallback(async () => {
    const [v, w] = await Promise.all([
      api.get("/vacation/requests").then(r => r.data.filter(x => x.employee_email !== user.email)),
      api.get("/wfh/requests").then(r => r.data.filter(x => x.employee_email !== user.email)),
    ]);
    setVacation(v); setWfh(w);
  }, [user.email]);
  useEffect(() => { load(); }, [load]);

  const decide = async () => {
    if (!open) return;
    setBusy(open.id);
    try {
      const base = open.type === "wfh" ? "/wfh/requests" : "/vacation/requests";
      await api.post(`${base}/${open.id}/${open.action}`, { comment });
      toast.success(open.action === "approve" ? "Suglasnost dana" : "Odbijeno");
      setOpen(null); setComment("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setBusy(null); }
  };

  const filterAndSort = useCallback((rows) => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? rows.filter(r => `${r.employee_name} ${r.employee_email} ${r.employee_katedra || ""}`.toLowerCase().includes(q))
      : rows;
    return [...filtered].sort((a, b) => {
      const da = new Date(a.created_at || 0).getTime();
      const db = new Date(b.created_at || 0).getTime();
      return sortDir === "desc" ? db - da : da - db;
    });
  }, [search, sortDir]);

  const filteredVacation = useMemo(() => filterAndSort(vacation), [vacation, filterAndSort]);
  const filteredWfh = useMemo(() => filterAndSort(wfh), [wfh, filterAndSort]);

  // Memoized derived state for "select all pending" checkbox + visibility
  const pendingVac = useMemo(() => filteredVacation.filter(r => r.status === "pending"), [filteredVacation]);
  const pendingWfh = useMemo(() => filteredWfh.filter(r => r.status === "pending"), [filteredWfh]);
  const allPendingVacSelected = useMemo(
    () => pendingVac.length > 0 && pendingVac.every(r => selectedVac.has(r.id)),
    [pendingVac, selectedVac]
  );
  const allPendingWfhSelected = useMemo(
    () => pendingWfh.length > 0 && pendingWfh.every(r => selectedWfh.has(r.id)),
    [pendingWfh, selectedWfh]
  );

  const toggleSel = (set, setter, id) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setter(next);
  };
  const toggleAllVac = (rows) => {
    const pending = rows.filter(r => r.status === "pending").map(r => r.id);
    if (pending.every(id => selectedVac.has(id))) setSelectedVac(new Set());
    else setSelectedVac(new Set(pending));
  };
  const toggleAllWfh = (rows) => {
    const pending = rows.filter(r => r.status === "pending").map(r => r.id);
    if (pending.every(id => selectedWfh.has(id))) setSelectedWfh(new Set());
    else setSelectedWfh(new Set(pending));
  };

  const bulkApprove = async (type) => {
    const ids = type === "vacation" ? [...selectedVac] : [...selectedWfh];
    if (ids.length === 0) { toast.error("Odaberite barem jedan zahtjev"); return; }
    setBulkBusy(true);
    try {
      const url = type === "vacation" ? "/vacation/bulk-approve" : "/wfh/bulk-approve";
      const r = await api.post(url, { ids });
      const { approved, failed } = r.data;
      toast.success(`Suglasnost dana: ${approved}${failed.length ? `, neuspješno: ${failed.length}` : ""}`);
      if (type === "vacation") setSelectedVac(new Set());
      else setSelectedWfh(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setBulkBusy(false); }
  };

  const bulkReject = async () => {
    const type = bulkRejectOpen;
    if (!type) return;
    const ids = type === "vacation" ? [...selectedVac] : [...selectedWfh];
    if (!bulkRejectReason.trim()) { toast.error("Razlog odbijanja je obavezan"); return; }
    setBulkBusy(true);
    try {
      const url = type === "vacation" ? "/vacation/bulk-reject" : "/wfh/bulk-reject";
      const r = await api.post(url, { ids, comment: bulkRejectReason.trim() });
      const { rejected, failed } = r.data;
      toast.success(`Odbijeno: ${rejected}${failed.length ? `, neuspješno: ${failed.length}` : ""}`);
      if (type === "vacation") setSelectedVac(new Set()); else setSelectedWfh(new Set());
      setBulkRejectOpen(null); setBulkRejectReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setBulkBusy(false); }
  };

  const VacationCard = (r) => (
    <Card key={r.id} data-testid={`approval-${r.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            {r.status === "pending" && (
              <input
                type="checkbox"
                className="mt-1 cursor-pointer w-4 h-4"
                checked={selectedVac.has(r.id)}
                onChange={() => toggleSel(selectedVac, setSelectedVac, r.id)}
                data-testid={`select-vac-${r.id}`}
                title="Odaberi za bulk akciju"
              />
            )}
            <div>
            <CardTitle className="text-lg">
              <button
                type="button"
                className="hover:text-primary text-left"
                onClick={() => setHistoryFor({ email: r.employee_email, full_name: r.employee_name, katedra: r.employee_katedra })}
                data-testid={`open-history-${r.employee_email}`}
                title="Otvori povijest zahtjeva"
              >
                {r.employee_name}
              </button>
            </CardTitle>
            <p className="text-xs text-muted-foreground">{r.employee_katedra} · {r.employee_email}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Predano: {hrDateTime(r.created_at)}</p>
          </div>
          </div>
          <Badge variant={statusBadgeVariant(r.status)}>{STATUS_LABEL[r.status]}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-sm"><span className="font-medium">Kolektivni:</span> {hr(r.collective_start)} – {hr(r.collective_end)}</div>
        {r.additional_periods?.length > 0 && (
          <div className="text-sm">
            <span className="font-medium">Dodatni periodi:</span>{" "}
            {r.additional_periods.map((p) => <span key={`${p.start}-${p.end}`} className="inline-block bg-secondary px-2 py-0.5 rounded mr-1">{hr(p.start)} – {hr(p.end)}</span>)}
          </div>
        )}
        <div className="text-sm"><span className="font-medium">Ukupno:</span> {r.total_days} radnih dana</div>
        {r.note && <div className="text-sm text-muted-foreground italic">"{r.note}"</div>}
        {r.manager_comment && <div className="text-sm border-l-2 border-accent pl-3"><span className="font-medium">Komentar voditelja:</span> {r.manager_comment}</div>}
        {r.status === "pending" && (
          <div className="flex gap-2 pt-2">
            <Button size="sm" onClick={() => setOpen({id: r.id, action: "approve", type: "vacation"})} data-testid={`approve-${r.id}`} disabled={busy===r.id}><Check className="w-4 h-4 mr-1" /> Suglasan</Button>
            <Button size="sm" variant="outline" onClick={() => setOpen({id: r.id, action: "reject", type: "vacation"})} data-testid={`reject-${r.id}`}><X className="w-4 h-4 mr-1" /> Odbij</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const WFHCard = (r) => (
    <Card key={r.id} data-testid={`wfh-approval-${r.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            {r.status === "pending" && (
              <input
                type="checkbox"
                className="mt-1 cursor-pointer w-4 h-4"
                checked={selectedWfh.has(r.id)}
                onChange={() => toggleSel(selectedWfh, setSelectedWfh, r.id)}
                data-testid={`select-wfh-${r.id}`}
                title="Odaberi za bulk akciju"
              />
            )}
            <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <HomeIcon className="w-4 h-4 text-accent"/>
              <button
                type="button"
                className="hover:text-primary text-left"
                onClick={() => setHistoryFor({ email: r.employee_email, full_name: r.employee_name, katedra: r.employee_katedra })}
                data-testid={`open-history-wfh-${r.employee_email}`}
                title="Otvori povijest zahtjeva"
              >
                {r.employee_name}
              </button>
            </CardTitle>
            <p className="text-xs text-muted-foreground">{r.employee_katedra} · {r.employee_email}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Predano: {hrDateTime(r.created_at)}</p>
          </div>
          </div>
          <Badge variant={statusBadgeVariant(r.status)}>{STATUS_LABEL[r.status]}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-sm"><span className="font-medium">Razdoblje:</span> {hr(r.start_date)} – {hr(r.end_date)}</div>
        {r.broj_mobitela && (
          <div className="text-sm"><span className="font-medium">Broj mobitela:</span> {r.broj_mobitela}</div>
        )}
        {r.razlog && (
          <div className="text-sm"><span className="font-medium">Napomena zaposlenika:</span> {r.razlog}</div>
        )}
        {r.dodatne_napomene && <div className="text-sm text-muted-foreground italic">„{r.dodatne_napomene}"</div>}
        {r.izjava_prihvacena && (
          <div className="text-xs text-emerald-700">✓ Izjava (4 točke) prihvaćena pri predaji.</div>
        )}
        {/* Legacy polja (samo za stare zapise prije Iter39) */}
        {r.lokacija && (
          <div className="text-xs text-muted-foreground"><span className="font-medium">Lokacija (stari zapis):</span> {r.lokacija}</div>
        )}
        {r.postotak_radnog_vremena && r.postotak_radnog_vremena !== 100 && (
          <div className="text-xs text-muted-foreground"><span className="font-medium">Postotak (stari zapis):</span> {r.postotak_radnog_vremena}%</div>
        )}
        {r.manager_comment && <div className="text-sm border-l-2 border-accent pl-3"><span className="font-medium">Komentar voditelja:</span> {r.manager_comment}</div>}
        {r.status === "pending" && (
          <div className="flex gap-2 pt-2">
            <Button size="sm" onClick={() => setOpen({id: r.id, action: "approve", type: "wfh"})} data-testid={`wfh-approve-${r.id}`} disabled={busy===r.id}><Check className="w-4 h-4 mr-1" /> Suglasan</Button>
            <Button size="sm" variant="outline" onClick={() => setOpen({id: r.id, action: "reject", type: "wfh"})} data-testid={`wfh-reject-${r.id}`}><X className="w-4 h-4 mr-1" /> Odbij</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="approvals-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="serif text-3xl sm:text-4xl font-semibold">Suglasnosti</h1>
          <p className="text-muted-foreground mt-1">Zahtjevi zaposlenika koji čekaju vašu suglasnost ili odbijanje</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {user?.can_submit_for_team && (
            <Button variant="default" size="sm" onClick={() => setBehalfOpen(true)}
                    data-testid="team-lead-behalf-btn">
              Predaj GO u ime zaposlenika
            </Button>
          )}
          <Button variant="outline" size="sm"
            onClick={() => setSortDir(d => d === "desc" ? "asc" : "desc")}
            data-testid="app-sort-toggle"
            title={`Sortirano: ${sortDir === "desc" ? "najnoviji prvo" : "najstariji prvo"}`}>
            {sortDir === "desc"
              ? <><ArrowDownWideNarrow className="w-4 h-4 mr-1.5"/> Najnoviji prvo</>
              : <><ArrowUpWideNarrow className="w-4 h-4 mr-1.5"/> Najstariji prvo</>}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-3 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Pretraži po imenu, e-mailu, katedri…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="app-search"
            />
          </div>
          {search && (
            <Button variant="ghost" size="sm" onClick={() => setSearch("")} data-testid="app-search-clear">
              <X className="w-4 h-4 mr-1"/>Očisti
            </Button>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="vacation">
        <TabsList>
          <TabsTrigger value="vacation" data-testid="tab-app-vacation">
            Godišnji odmor ({filteredVacation.length}{filteredVacation.length !== vacation.length ? ` od ${vacation.length}` : ""})
          </TabsTrigger>
          <TabsTrigger value="wfh" data-testid="tab-app-wfh">
            Rad od kuće ({filteredWfh.length}{filteredWfh.length !== wfh.length ? ` od ${wfh.length}` : ""})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="vacation" className="space-y-3 mt-4">
          {pendingVac.length > 0 && (
            <Card className="border-accent/40 bg-accent/5">
              <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                <label className="text-xs flex items-center gap-2 cursor-pointer">
                  <input type="checkbox"
                    checked={allPendingVacSelected}
                    onChange={() => toggleAllVac(filteredVacation)}
                    className="w-4 h-4" data-testid="select-all-vac"/>
                  <span>Odaberi sve <i>pending</i></span>
                </label>
                <span className="text-xs text-muted-foreground">Odabrano: <b>{selectedVac.size}</b></span>
                <Button size="sm" disabled={selectedVac.size === 0 || bulkBusy}
                  onClick={() => bulkApprove("vacation")}
                  data-testid="bulk-approve-vac">
                  {bulkBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <CheckCheck className="w-4 h-4 mr-1"/>}
                  Skupna suglasnost ({selectedVac.size})
                </Button>
                <Button size="sm" variant="destructive" disabled={selectedVac.size === 0 || bulkBusy}
                  onClick={() => setBulkRejectOpen("vacation")}
                  data-testid="bulk-reject-vac">
                  <X className="w-4 h-4 mr-1"/>Bulk odbij ({selectedVac.size})
                </Button>
              </CardContent>
            </Card>
          )}
          {filteredVacation.length === 0
            ? <Card><CardContent className="p-12 text-center text-muted-foreground">
                {search ? `Nema rezultata za "${search}".` : "Trenutno nema zahtjeva za pregled."}
              </CardContent></Card>
            : filteredVacation.map(VacationCard)}
        </TabsContent>
        <TabsContent value="wfh" className="space-y-3 mt-4">
          {pendingWfh.length > 0 && (
            <Card className="border-accent/40 bg-accent/5">
              <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                <label className="text-xs flex items-center gap-2 cursor-pointer">
                  <input type="checkbox"
                    checked={allPendingWfhSelected}
                    onChange={() => toggleAllWfh(filteredWfh)}
                    className="w-4 h-4" data-testid="select-all-wfh"/>
                  <span>Odaberi sve <i>pending</i></span>
                </label>
                <span className="text-xs text-muted-foreground">Odabrano: <b>{selectedWfh.size}</b></span>
                <Button size="sm" disabled={selectedWfh.size === 0 || bulkBusy}
                  onClick={() => bulkApprove("wfh")}
                  data-testid="bulk-approve-wfh">
                  {bulkBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <CheckCheck className="w-4 h-4 mr-1"/>}
                  Skupna suglasnost ({selectedWfh.size})
                </Button>
                <Button size="sm" variant="destructive" disabled={selectedWfh.size === 0 || bulkBusy}
                  onClick={() => setBulkRejectOpen("wfh")}
                  data-testid="bulk-reject-wfh">
                  <X className="w-4 h-4 mr-1"/>Bulk odbij ({selectedWfh.size})
                </Button>
              </CardContent>
            </Card>
          )}
          {filteredWfh.length === 0
            ? <Card><CardContent className="p-12 text-center text-muted-foreground">
                {search ? `Nema rezultata za "${search}".` : "Nema zahtjeva za rad od kuće."}
              </CardContent></Card>
            : filteredWfh.map(WFHCard)}
        </TabsContent>
      </Tabs>

      <ApprovalDecisionDialog
        open={open}
        comment={comment}
        onCommentChange={setComment}
        onClose={() => { setOpen(null); setComment(""); }}
        onConfirm={decide}
        busy={busy}
      />

      <EmployeeHistorySheet employee={historyFor} onClose={() => setHistoryFor(null)} />

      <BulkRejectDialog
        open={bulkRejectOpen}
        count={bulkRejectOpen === "vacation" ? selectedVac.size : selectedWfh.size}
        reason={bulkRejectReason}
        onReasonChange={setBulkRejectReason}
        onClose={() => { setBulkRejectOpen(null); setBulkRejectReason(""); }}
        onConfirm={bulkReject}
        busy={bulkBusy}
      />

      {/* Iter91: team-lead predaje u ime člana svog tima */}
      {user?.can_submit_for_team && (
        <BehalfVacationDialog
          open={behalfOpen}
          pickEmployee={true}
          teamOnly={true}
          currentUserEmail={user.email}
          onClose={() => setBehalfOpen(false)}
          onSuccess={() => { setBehalfOpen(false); load(); }}
        />
      )}
    </div>
  );
}
